import { Bot, RefreshCw, Home, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

/**
 * Page-level error fallback - Full screen for catastrophic errors
 * Used at route level to catch entire page crashes
 */
export function PageErrorFallback({ error, resetErrorBoundary }: ErrorFallbackProps) {
  const { t } = useTranslation('errors');
  const isDev = import.meta.env.DEV;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20">
            <Bot className="h-8 w-8 text-primary animate-bounce" />
          </div>
          <CardTitle className="text-xl">{t('errorBoundary.title')}</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {t('errorBoundary.subtitle')}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-center text-muted-foreground">
            {t('errorBoundary.description')}
          </p>

          {isDev && (
            <div className="rounded-md bg-muted p-3 text-sm">
              <p className="font-mono text-xs text-destructive break-all">
                {error.message}
              </p>
            </div>
          )}

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              onClick={resetErrorBoundary}
              className="flex-1"
              variant="default"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('errorBoundary.tryAgain')}
            </Button>
            <Button
              onClick={() => window.location.href = '/dashboard'}
              className="flex-1"
              variant="outline"
            >
              <Home className="mr-2 h-4 w-4" />
              {t('errorBoundary.goToDashboard')}
            </Button>
          </div>

          <Button
            onClick={() => window.location.reload()}
            variant="ghost"
            className="w-full text-muted-foreground"
          >
            {t('errorBoundary.reloadPage')}
          </Button>

          {/* If issue persists section */}
          <div className="mt-6 pt-4 border-t border-border">
            <div className="flex items-start gap-2 text-sm">
              <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-foreground">
                  {t('errorBoundary.persistsTitle')}
                </p>
                <p className="text-muted-foreground text-xs mt-1">
                  {t('errorBoundary.persistsDescription')}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Widget-level error fallback - Compact card for isolated failures
 * Used to wrap individual widgets/components that might fail
 */
export function WidgetErrorFallback({ error, resetErrorBoundary }: ErrorFallbackProps) {
  const { t } = useTranslation('errors');
  const isDev = import.meta.env.DEV;

  return (
    <div className="flex flex-col items-center justify-center p-4 rounded-lg border border-primary/20 bg-primary/5 dark:bg-primary/10 min-h-[120px]">
      <Bot className="h-6 w-6 text-primary mb-2" />
      <p className="text-sm text-muted-foreground text-center mb-2">
        {t('errorBoundary.widgetTitle')}
      </p>
      {isDev && (
        <p className="text-xs font-mono text-destructive/70 mb-2 max-w-full truncate px-2">
          {error.message}
        </p>
      )}
      <Button
        onClick={resetErrorBoundary}
        variant="outline"
        size="sm"
      >
        <RefreshCw className="mr-1 h-3 w-3" />
        {t('errorBoundary.widgetRetry')}
      </Button>
    </div>
  );
}
